import React from 'react';
import { Helmet } from 'react-helmet-async';
import { PhoneCall, CheckCircle, ArrowRight } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { navigateToStartHere } from '../utils/navigation';

export function AutomationPage() {
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <>
      <Helmet>
        <title>Lead Rescue & Automation - Priority AI Services</title>
        <meta
          name="description"
          content="Stop losing bookings from missed calls and manual follow-up. Simple systems that catch missed calls and plug them into your booking process automatically."
        />
      </Helmet>

      <div className="min-h-screen pt-24 pb-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Hero */}
          <div className="text-center mb-16">
            <PhoneCall className="w-16 h-16 text-blue-500 mx-auto mb-6" />
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Stop losing bookings from missed calls and manual follow-up.
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed mb-8">
              For busy clinics with phones ringing, full diaries, and receptionists who can't chase every lead. I build simple systems that catch missed calls and plug them into your booking process automatically.
            </p>
            <button
              onClick={() => navigateToStartHere(navigate, location.pathname)}
              className="inline-flex items-center gap-2 px-8 py-4 bg-blue-500 text-white text-lg font-semibold rounded-md hover:bg-blue-600 transition-colors"
            >
              Start Here
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>

          {/* Who this is for */}
          <div className="bg-gray-900/50 backdrop-blur-sm border border-blue-500/20 rounded-lg p-8 mb-12">
            <h2 className="text-3xl font-bold text-white mb-6">Who this is for</h2>
            <p className="text-gray-300 mb-4 leading-relaxed">
              This is for clinics that:
            </p>
            <ul className="space-y-3">
              <li className="flex items-start gap-3 text-gray-300">
                <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                <span>Get a steady flow of calls and inquiries.</span>
              </li>
              <li className="flex items-start gap-3 text-gray-300">
                <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                <span>Have reception/admin staff who are stretched thin.</span>
              </li>
              <li className="flex items-start gap-3 text-gray-300">
                <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                <span>Know missed calls and slow follow-up are costing them money.</span>
              </li>
              <li className="flex items-start gap-3 text-gray-300">
                <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                <span>Want better systems, not another app to babysit.</span>
              </li>
            </ul>
          </div>

          {/* Where clinics quietly lose money */}
          <div className="bg-black/40 border border-blue-500/20 rounded-lg p-8 mb-12">
            <h2 className="text-3xl font-bold text-white mb-6">Where clinics quietly lose money</h2>
            <div className="space-y-4 text-gray-300">
              <p className="leading-relaxed">
                <strong className="text-white">Patients hitting voicemail and never calling back.</strong> Someone calls because they're in pain right now. If they hit voicemail, they're calling the next clinic. You've lost them before you even knew they existed.
              </p>
              <p className="leading-relaxed">
                <strong className="text-white">Promised follow-ups that don't happen.</strong> Your receptionist writes down "call back tomorrow" on a sticky note. Tomorrow comes, the phone rings 40 times, and that sticky note gets buried under five others. The lead goes cold.
              </p>
              <p className="leading-relaxed">
                <strong className="text-white">No clear way to see how many missed calls turned into bookings.</strong> You know you're busy, but you have no idea how much revenue is slipping through the cracks because there's no system tracking it.
              </p>
            </div>
          </div>

          {/* The Lead Rescue system */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">The Lead Rescue system I build for you</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-gray-900/50 backdrop-blur-sm border border-blue-500/20 rounded-lg p-6">
                <h3 className="text-xl font-bold text-white mb-4">Smart call handling</h3>
                <ul className="space-y-2 text-gray-300">
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0" />
                    <span>Detects missed calls in real-time.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0" />
                    <span>Integrates with your existing phone system (no new numbers or hardware).</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0" />
                    <span>Works with your calendar/booking tool, not against it.</span>
                  </li>
                </ul>
              </div>

              <div className="bg-gray-900/50 backdrop-blur-sm border border-blue-500/20 rounded-lg p-6">
                <h3 className="text-xl font-bold text-white mb-4">Instant text-back</h3>
                <ul className="space-y-2 text-gray-300">
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0" />
                    <span>Sends an immediate, personalized text to every missed caller.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0" />
                    <span>Acknowledges their call, offers to book, and keeps them engaged.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0" />
                    <span>Written in your clinic's voice, not robotic template language.</span>
                  </li>
                </ul>
              </div>

              <div className="bg-gray-900/50 backdrop-blur-sm border border-blue-500/20 rounded-lg p-6">
                <h3 className="text-xl font-bold text-white mb-4">Lead logging</h3>
                <ul className="space-y-2 text-gray-300">
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0" />
                    <span>Every lead captured in one central dashboard.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0" />
                    <span>See exactly who called, when, and what happened next.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0" />
                    <span>No more sticky notes, scattered texts, or guessing who needs follow-up.</span>
                  </li>
                </ul>
              </div>

              <div className="bg-gray-900/50 backdrop-blur-sm border border-blue-500/20 rounded-lg p-6">
                <h3 className="text-xl font-bold text-white mb-4">Simple reporting</h3>
                <ul className="space-y-2 text-gray-300">
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0" />
                    <span>Weekly/monthly summaries of missed calls, responses, and bookings.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0" />
                    <span>See exactly how much revenue you're saving vs. losing.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0" />
                    <span>Clear data to justify the investment and make smarter decisions.</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* How working together works */}
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-8 mb-12">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">How working together works</h2>
            <div className="space-y-6 max-w-2xl mx-auto">
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold text-lg">
                  1
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-2">Systems audit</h3>
                  <p className="text-gray-300">
                    We look at your current call volume, booking process, and pain points. I map out exactly where you're losing leads and what it's costing you.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold text-lg">
                  2
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-2">Plan around your tools</h3>
                  <p className="text-gray-300">
                    I design the system to work with your existing phone, calendar, and booking software—not replace it. You approve the message templates and flow before anything goes live.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold text-lg">
                  3
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-2">Build, test, go live</h3>
                  <p className="text-gray-300">
                    I set everything up, test it with real calls, and train your team on the dashboard. Once live, the system runs on autopilot and I monitor performance monthly.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center bg-blue-500/10 border border-blue-500/30 rounded-lg p-12 mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">Ready to get started?</h2>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              Fill out the Start Here form and we'll discuss how this can work for your clinic.
            </p>
            <button
              onClick={() => navigateToStartHere(navigate, location.pathname)}
              className="inline-flex items-center gap-2 px-8 py-4 bg-blue-500 text-white text-lg font-semibold rounded-md hover:bg-blue-600 transition-colors"
            >
              Start Here
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
